package guide.kuki.app.engine

import kotlinx.serialization.Serializable

/**
 * A radial section spec: [divs] blocks each [dr] metres long along the
 * radial within its region. [dr] = -1 means "auto" — the section's
 * length is whatever's left in its region after the fixed sections are
 * accounted for, divided evenly per block across all auto sections in
 * the same region.
 */
@Serializable
data class Section(
    val divs: Int,
    val dr: Double,
)

/**
 * `map_sections.json` — one list of section specs per inter-anchor
 * region. With 3 anchors per radial there are 2 regions (reef → beach
 * and beach → centre), so this list has 2 entries.
 */
@Serializable
data class MapSectionsData(
    val sections: List<List<Section>>,
)

/**
 * `map_radials.json` — radial anchor coordinates. Each radial is N+1
 * `[lat, lon]` anchor points, where N is the number of inter-anchor
 * regions in [MapSectionsData.sections]. The engine generates the per-
 * section boundary points at load time via [expandRadial].
 */
@Serializable
data class MapRadialsData(
    val radials: List<List<List<Double>>>,
)

/**
 * JSON structure for a single named location entry in names.json.
 */
@Serializable
data class NameEntry(
    val name: String = "",
    val meaning: String = "",
)

/**
 * Builds a [RadialMap] from the two new-format files. The flat
 * `divs.flatten()` matches the old single-list `sections: [Int]`
 * format the engine was built around, so [Slice]/[Block] math is
 * unchanged.
 */
fun buildRadialMap(
    radialsAnchors: List<List<List<Double>>>,
    sectionGroups: List<List<Section>>,
): RadialMap {
    val divs = sectionGroups.flatMap { group -> group.map { it.divs } }
    val expanded = radialsAnchors.map { anchors ->
        expandRadial(anchors.map { pt -> doubleArrayOf(pt[0], pt[1]) }, sectionGroups)
    }
    return RadialMap(expanded, divs)
}

/**
 * Generates one radial's full boundary-point list from its anchors and
 * the per-region section specs. Each region's intermediate boundaries
 * sit on the straight line between its two anchors (same as the
 * Python generator's `push_radial`); the last point of each region
 * snaps to the next anchor so floating-point drift doesn't move it.
 *
 * Returns 1 + Σ(sectionGroups[i].size) points — one per radial-grid
 * boundary, with the outer anchor first and the inner anchor last.
 */
fun expandRadial(
    anchors: List<DoubleArray>,
    sectionGroups: List<List<Section>>,
): List<DoubleArray> {
    require(anchors.size == sectionGroups.size + 1) {
        "anchor count (${anchors.size}) must equal section-group count + 1 " +
            "(${sectionGroups.size + 1})"
    }
    val points = mutableListOf(anchors[0])
    for (regionIdx in sectionGroups.indices) {
        val start = anchors[regionIdx]
        val end = anchors[regionIdx + 1]
        val sections = sectionGroups[regionIdx]
        if (sections.isEmpty()) {
            points.add(end)
            continue
        }
        val total = distance(start, end)
        val fixedLength = sections.asSequence().filter { it.dr >= 0 }.sumOf { it.divs * it.dr }
        val autoDivs = sections.asSequence().filter { it.dr < 0 }.sumOf { it.divs }
        val autoDr = if (autoDivs > 0) (total - fixedLength) / autoDivs else 0.0
        var traveled = 0.0
        for (i in sections.indices) {
            val sec = sections[i]
            val dr = if (sec.dr >= 0) sec.dr else autoDr
            traveled += sec.divs * dr
            if (i == sections.lastIndex) {
                points.add(end)
            } else {
                points.add(pushRadial(start, end, traveled))
            }
        }
    }
    return points
}

/**
 * Linear interpolation in lat/lon, [dist] metres from [p0] toward
 * [p1]. Matches the Python generator's `push_radial` so the engine
 * produces the same geometry the source `radial_map_100min.json` was
 * built from.
 */
private fun pushRadial(p0: DoubleArray, p1: DoubleArray, dist: Double): DoubleArray {
    val d01 = distance(p0, p1)
    if (d01 <= 0.0) return doubleArrayOf(p0[0], p0[1])
    val xi = dist / d01
    return doubleArrayOf(
        p0[0] + xi * (p1[0] - p0[0]),
        p0[1] + xi * (p1[1] - p0[1]),
    )
}

/**
 * Converts parsed name entries into the flat map expected by [RadialMap.loadAddresses].
 * Keys are like "a1", "ka2", "ngo1", etc. Values are the name strings.
 */
fun Map<String, NameEntry>.toAddressNames(): Map<String, String> {
    return mapValues { it.value.name }
}
